import React, { useState, useEffect } from 'react';
import { UserPlus, Check, X, Mail } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { supabase } from '../../lib/supabase';
import { translate } from '../../lib/translations';
import toast from 'react-hot-toast';
import { sendJoinRequestDecisionEmail, sendLeagueInvitationEmail } from '../../lib/api/emailNotifications';

interface JoinRequest {
  league_id: number;
  user_id: number;
  username: string;
  email?: string;
}

interface JoinRequestsProps {
  language: 'en' | 'fr';
}

export function JoinRequests({ language }: JoinRequestsProps) {
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const [processing, setProcessing] = useState<number | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  useEffect(() => {
    if (!selectedLeague) return;

    async function fetchRequests() {
      try {
        const { data: inviteData, error } = await supabase
          .from('league_invitation')
          .select('league_id, user_id, username')
          .eq('league_id', selectedLeague.id);

        if (error) throw error;

        // Fetch emails for each requester
        const withEmails: JoinRequest[] = await Promise.all(
          (inviteData || []).map(async (req) => {
            const { data: userData } = await supabase
              .from('users')
              .select('email')
              .eq('user_id', req.user_id)
              .maybeSingle();
            return { ...req, email: userData?.email || '' };
          })
        );

        setRequests(withEmails);
      } catch (error) {
        console.error('Error fetching join requests:', error);
        toast.error(translate('failed.load.requests', language));
      } finally {
        setLoading(false);
      }
    }

    fetchRequests();
  }, [selectedLeague, language]);

  const handleApprove = async (userId: number, username: string) => {
    if (!selectedLeague) return;
    setProcessing(userId);

    try {
      // Add user to league
      const { error: joinError } = await supabase.rpc('add_user_to_league', {
        p_user_id: userId,
        p_league_id: selectedLeague.id
      });

      if (joinError) throw joinError;

      // Remove invitation
      const { error: deleteError } = await supabase
        .from('league_invitation')
        .delete()
        .eq('league_id', selectedLeague.id)
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      toast.success(translate('approved.request', language, { username }));
      const approved = requests.find(r => r.user_id === userId);
      setRequests(requests.filter(request => request.user_id !== userId));

      // Email non-bloquant au demandeur
      if (approved?.email) {
        try {
          await sendJoinRequestDecisionEmail({
            userEmail: approved.email,
            username: approved.username,
            leagueName: selectedLeague.name,
            isApproved: true,
          });
        } catch (emailErr) {
          console.error('Join approval email failed (non-blocking):', emailErr);
        }
      }
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(translate('failed.approve', language));
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (userId: number, username: string) => {
    if (!selectedLeague) return;
    setProcessing(userId);

    try {
      const { error } = await supabase
        .from('league_invitation')
        .delete()
        .eq('league_id', selectedLeague.id)
        .eq('user_id', userId);

      if (error) throw error;

      toast.success(translate('rejected.request', language, { username }));
      const rejected = requests.find(r => r.user_id === userId);
      setRequests(requests.filter(request => request.user_id !== userId));

      // Email non-bloquant au demandeur
      if (rejected?.email) {
        try {
          await sendJoinRequestDecisionEmail({
            userEmail: rejected.email,
            username: rejected.username,
            leagueName: selectedLeague.name,
            isApproved: false,
          });
        } catch (emailErr) {
          console.error('Join rejection email failed (non-blocking):', emailErr);
        }
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(translate('failed.reject', language));
    } finally {
      setProcessing(null);
    }
  };

  const handleSendInvite = async () => {
    if (!selectedLeague || !inviteEmail.trim()) return;
    setSendingInvite(true);

    try {
      // Fetch commissioner username
      const { data: leagueData } = await supabase
        .from('leagues')
        .select('admin_user_id')
        .eq('league_id', selectedLeague.id)
        .maybeSingle();

      let commissionerUsername = 'Le commissaire';
      if (leagueData?.admin_user_id) {
        const { data: commData } = await supabase
          .from('users')
          .select('username')
          .eq('user_id', leagueData.admin_user_id)
          .maybeSingle();
        if (commData?.username) commissionerUsername = commData.username;
      }

      await sendLeagueInvitationEmail({
        inviteeEmail: inviteEmail.trim(),
        leagueName: selectedLeague.name,
        commissionerUsername,
        leagueId: selectedLeague.id,
      });

      toast.success(`Invitation envoyée à ${inviteEmail.trim()}`);
      setInviteEmail('');
    } catch (error) {
      console.error('Error sending invite:', error);
      toast.error("Erreur lors de l'envoi de l'invitation");
    } finally {
      setSendingInvite(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{translate('processing', language)}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="join-requests">
      {/* Invite a manager by email */}
      <div className="card border shadow-sm mb-4">
        <div className="card-body">
          <h6 className="d-flex align-items-center mb-3">
            <Mail size={16} className="text-primary me-2" />
            Inviter un gérant par email
          </h6>
          <div className="d-flex gap-2">
            <input
              type="email"
              className="form-control"
              placeholder="email@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendInvite()}
            />
            <button
              className="btn btn-primary d-flex align-items-center"
              onClick={handleSendInvite}
              disabled={sendingInvite || !inviteEmail.trim()}
            >
              {sendingInvite ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <>
                  <Mail size={16} className="me-2" />
                  Inviter
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="text-center p-5">
          <p className="text-muted">{translate('no.join.requests', language)}</p>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {requests.map((request) => (
            <div key={request.user_id} className="card border shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <UserPlus size={20} className="text-primary me-2" />
                    <div>
                      <h5 className="card-title mb-0">{request.username}</h5>
                    </div>
                  </div>
                </div>
                
                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    className="btn btn-success d-flex align-items-center"
                    onClick={() => handleApprove(request.user_id, request.username)}
                    disabled={processing === request.user_id}
                  >
                    {processing === request.user_id ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        {translate('processing', language)}
                      </>
                    ) : (
                      <>
                        <Check size={18} className="me-2" />
                        {translate('approve', language)}
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-danger d-flex align-items-center"
                    onClick={() => handleReject(request.user_id, request.username)}
                    disabled={processing === request.user_id}
                  >
                    {processing === request.user_id ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        {translate('processing', language)}
                      </>
                    ) : (
                      <>
                        <X size={18} className="me-2" />
                        {translate('reject', language)}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}